import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Tab, Tabs } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

import { ApplicationState } from 'store/store';
import { MainLayout } from 'layouts/MainLayout';
import { CustomerReviews } from './CustomerReviews';
import { ReviewsOverview } from './ReviewsOverview';
import { ReviewsOffPage } from './ReviewsOffPage';
import { ReviewsSettingsPanel } from 'components/reviews/settings/ReviewsSettingsPanel';
import { getAllLocationTitles } from 'store/locations/locationsSlice';
import { useAppSelector } from 'store/hooks';
import { Permission, Resource } from '@pepperhq/auth-client';

type ReviewsTab = 'customer' | 'overview';

const isReviewsTab = (value: string | null): value is ReviewsTab =>
    value === 'customer' || value === 'overview';

export const ReviewsPage: React.FC = () => {
    const dispatch = useDispatch();
    const { settings, loading: isLoadingSettings } = useSelector((state: ApplicationState) => state.settings);
    const isReviewsEnabled = React.useMemo(
        () => settings?.reviewsEnabled ?? false,
        [settings?.reviewsEnabled]
    );
    const { claims } = useAppSelector(state => state.auth);
    const hasReviewsReadPermission = React.useMemo(
        () =>
            claims.hasPermission(Resource.Setting, Permission.read) &&
            claims.hasPermission(Resource.Reviews, Permission.read),
        [claims]
    );
    const hasReviewsWritePermission = React.useMemo(
        () => claims.hasPermission(Resource.Reviews, Permission.write),
        [claims]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const tab = React.useMemo<ReviewsTab>(() => {
        const value = new URLSearchParams(location.search).get('tab');
        return isReviewsTab(value) ? value : 'overview';
    }, [location.search]);
    const [showSettings, setShowSettings] = React.useState(false);

    React.useEffect(() => {
        getAllLocationTitles(dispatch);
    }, [dispatch]);

    const handleTabChange = React.useCallback(
        (_: React.SyntheticEvent, newValue: ReviewsTab) => {
            const params = new URLSearchParams(location.search);
            params.set('tab', newValue);
            navigate({ search: params.toString() }, { replace: true });
        },
        [location.search, navigate]
    );

    const handleSettingsClick = React.useCallback(() => {
        setShowSettings(true);
    }, []);

    const handleSettingsClose = React.useCallback(() => {
        setShowSettings(false);
    }, []);

    return (
        <MainLayout
            pageName="Reviews"
            pageDescription="Turn happy customers into 5-star Google reviews."
            noScroll
            headerActions={
                isReviewsEnabled &&
                hasReviewsReadPermission && (
                    <Button
                        sx={{ marginLeft: 'auto', gap: 1 }}
                        variant="outlined"
                        onClick={handleSettingsClick}
                    >
                        Settings <SettingsIcon />
                    </Button>
                )
            }
        >
            {!isLoadingSettings && (
                <>
                    {isReviewsEnabled && (
                        <Box display="flex" flexDirection="column" height="100%">
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs
                                    variant="fullWidth"
                                    value={tab}
                                    indicatorColor="primary"
                                    onChange={handleTabChange}
                                >
                                    <Tab label="Dashboard" value="overview" />
                                    <Tab label="Customer Reviews" value="customer" />
                                </Tabs>
                            </Box>
                            {tab === 'customer' && <CustomerReviews />}
                            {tab === 'overview' && <ReviewsOverview />}
                        </Box>
                    )}
                    {!isReviewsEnabled && <ReviewsOffPage />}
                    {hasReviewsReadPermission && (
                        <ReviewsSettingsPanel
                            open={showSettings}
                            onClose={handleSettingsClose}
                            hasReviewsWritePermission={hasReviewsWritePermission}
                        />
                    )}
                </>
            )}
        </MainLayout>
    );
};
